import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { differenceInCalendarDays, parseISO, isValid } from "date-fns";
import Sidebar from "../components/Nav/AdminSidebar";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaGooglePay,
  FaPhone,
  FaCreditCard,
  FaCalendarAlt,
  FaBed,
  FaArrowLeft,
  FaSnowflake,
  FaDoorClosed,
  FaDoorOpen,
  FaHome,
  FaUsers,
  FaRegStickyNote,
} from "react-icons/fa";
import GooglePayButton from "../components/payments/googlePayPage";
import "react-datepicker/dist/react-datepicker.css";
import DatePicker from "react-datepicker";

const getImageForCategory = (categoryId) => {
  const images = {
    1: "/assets/rooms/suite/suite.jpg",
    2: "/assets/rooms/double/double.png",
    3: "/assets/rooms/double/no-ac.jpg",
    5: "/assets/rooms/single/ac.jpeg",
    6: "/assets/rooms/single/no-ac.jpeg",
  };
  return images[categoryId] || "/assets/rooms/default.jpg";
};

const getFeaturesFromCategory = (categoryName) => {
  let type = "Single Room";
  if (categoryName.includes("Suite")) type = "Suite";
  else if (categoryName.includes("Double Room")) type = "Double Room";

  const ac = categoryName.includes("No AC") ? "No AC" : "AC";
  const bath = categoryName.includes("Non-attached")
    ? "Non-attached Bathroom"
    : "Attached Bathroom";

  return { type, ac, bath };
};

export default function BookingPage() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const today = new Date().toISOString().split("T")[0];
  const tomorrow = new Date(Date.now() + 86400000).toISOString().split("T")[0];
  const [room, setRoom] = useState(null);
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [numDays, setNumDays] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState("");
  const BASE_URL = process.env.REACT_APP_SERVER_BASE_URL;
  const [visitPurpose, setVisitPurpose] = useState("");
  const [disabledDates, setDisabledDates] = useState([]);

  const [numPeople, setNumPeople] = useState(1);

  useEffect(() => {
    fetch(
      `${BASE_URL}/room/getRoomByIDWithBookings.php?room_id=${encodeURIComponent(
        roomId
      )}`
    )
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setRoom(data.data.room);

          // Extract booked date ranges
          const allDisabled = [];
          data.data.bookings.forEach((booking) => {
            const start = new Date(booking.check_in);
            const end = new Date(booking.check_out);

            for (
              let d = new Date(start);
              d <= end;
              d.setDate(d.getDate() + 1)
            ) {
              allDisabled.push(new Date(d));
            }
          });

          setDisabledDates(allDisabled);
        } else {
          toast.error(data.message || "Failed to load room details");
          navigate("/");
        }
      })
      .catch(() => {
        toast.error("Failed to load room details");
        navigate("/");
      });
  }, [roomId, navigate]);

  useEffect(() => {
    if (checkIn && checkOut) {
      const start = parseISO(checkIn);
      const end = parseISO(checkOut);
      // console.log(totalPrice.toFixed(2));
      if (isValid(start) && isValid(end)) {
        const days = differenceInCalendarDays(end, start);
        if (days > 0) {
          setNumDays(days);
          setTotalPrice(days * room.price);
          return;
        }
      }
    }
    setNumDays(0);
    setTotalPrice(0);
  }, [checkIn, checkOut, room]);

  const handlePayment = () => {
    if (!paymentMethod) {
      toast.error("Please select a payment method");
      return;
    }
    if (numDays === 0) {
      toast.error("Please select valid check-in and check-out dates");
      return;
    }
    if (paymentMethod === "Google Pay") {
      navigate(`/googlepay?amount=${totalPrice.toFixed(2)}`);
    }
  };

  if (!room)
    return (
      <div className="flex justify-center items-center min-h-screen text-xl font-semibold text-gray-600">
        Loading room info...
      </div>
    );

  const features = getFeaturesFromCategory(room.category_name);

  // Google Pay payment (impt)
  const onPaymentSuccess = async (paymentData) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_BASE_URL}/payment/transaction-confirmation.php`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            ...paymentData,
            amount: parseFloat(totalPrice.toFixed(2)),
            numPeople,
            roomId,
            purposeOfVisit: visitPurpose,
            checkIn,
            checkOut,
          }),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        throw new Error(
          `HTTP error! status: ${response.status}, body: ${text}`
        );
      }

      const data = await response.json();

      if (data.success) {
        toast.success(`Booking successful!`);
      } else {
        toast.error("Payment verification failed: " + data.message);
      }
    } catch (err) {
      toast.error("Payment processing error: " + err.message);
      console.error(err);
    }
  };

  const onPaymentError = (error) => {
    toast.error("Payment cancelled or failed");
    console.error(error);
  };
  return (
    <div className="min-h-screen bg-gradient-to-r from-orange-50 to-orange-100 flex flex-col">
      {/* Sticky Header */}
      <header className="sticky top-0 bg-white shadow-md z-20 p-5 flex items-center gap-4">
        {/* Back Arrow */}
        <button
          onClick={() => navigate("/")}
          className="text-orange-600 text-xl hover:text-orange-800 transition"
          aria-label="Go back to home"
        >
          <FaArrowLeft />
        </button>

        <img
          src={getImageForCategory(room.category_id)}
          alt={`Room ${room.room_no}`}
          className="w-16 h-16 rounded-lg object-cover shadow-sm"
        />
        <div>
          <h1 className="text-2xl font-bold text-orange-600 flex items-center gap-2">
            <FaBed /> Room {room.room_no}
          </h1>
          <p className="text-gray-700 font-medium">{room.category_name}</p>
        </div>
      </header>

      <main className="flex-grow overflow-auto p-6 max-w-7xl mx-auto w-full flex flex-col md:flex-row gap-10">
        {/* Left Side: Room Details */}
        <section className="md:w-1/2 bg-white rounded-2xl p-8 shadow-md flex flex-col gap-6">
          <img
            src={getImageForCategory(room.category_id)}
            alt={`Room ${room.room_no}`}
            className="w-full h-64 rounded-xl object-cover shadow-lg"
          />
          <div>
            <h2 className="text-xl font-semibold text-orange-600 mb-4 flex items-center gap-2">
              <FaHome /> Features
            </h2>
            <ul className="space-y-3 text-gray-700 text-lg">
              <li className="flex items-center gap-3">
                {features.type === "Suite" ? (
                  <FaUsers className="text-orange-500" />
                ) : (
                  <FaBed className="text-pink-500" />
                )}
                <span>{features.type}</span>
              </li>
              <li className="flex items-center gap-3">
                {features.ac === "AC" ? (
                  <FaSnowflake className="text-blue-500" />
                ) : (
                  <FaSnowflake className="text-gray-400 line-through" />
                )}
                <span>{features.ac}</span>
              </li>
              <li className="flex items-center gap-3">
                {features.bath === "Attached Bathroom" ? (
                  <FaDoorClosed className="text-green-600" />
                ) : (
                  <FaDoorOpen className="text-red-600" />
                )}
                <span>{features.bath}</span>
              </li>
            </ul>
          </div>
          <p className="text-2xl font-bold text-orange-600 flex items-center gap-2">
            ₹{room.price} / night
          </p>
        </section>

        {/* Right Side: Booking Form */}
        <section className="md:w-1/2 bg-white rounded-2xl p-8 shadow-md flex flex-col justify-between">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-8">
              Book Your Stay
            </h2>

            {/* Dates */}
            <div className="grid grid-cols-12 gap-3 mb-4 items-end">
              {/* Check-in */}
              <div className="col-span-12 sm:col-span-4">
                <label
                  htmlFor="checkin"
                  className="block mb-2 font-semibold text-gray-700 flex items-center gap-2"
                >
                  <FaCalendarAlt className="text-orange-500" /> Check-in{" "}
                  <span className="text-red-500 text-md font-normal">*</span>
                </label>
                <DatePicker
                  selected={checkIn ? new Date(checkIn) : null}
                  onChange={(date) => {
                    setCheckIn(date.toISOString().split("T")[0]);

                    const minCheckout = new Date(date.getTime() + 86400000);
                    if (!checkOut || new Date(checkOut) <= date) {
                      setCheckOut(minCheckout.toISOString().split("T")[0]);
                    }
                  }}
                  excludeDates={disabledDates}
                  minDate={new Date()}
                  placeholderText="dd/mm/yy"
                  dateFormat="dd/MM/yyyy"
                  className="w-full border border-gray-300 rounded-lg p-3"
                />
              </div>

              {/* Check-out */}
              <div className="col-span-12 sm:col-span-4">
                <label
                  htmlFor="checkout"
                  className="block mb-2 font-semibold text-gray-700 flex items-center gap-2"
                >
                  <FaCalendarAlt className="text-orange-500" /> Check-out{" "}
                  <span className="text-red-500 text-md font-normal">*</span>
                </label>
                <DatePicker
                  selected={checkOut ? new Date(checkOut) : null}
                  onChange={(date) => {
                    const minCheckout = new Date(
                      new Date(checkIn).getTime() + 86400000
                    );
                    if (date < minCheckout) {
                      setCheckOut(minCheckout.toISOString().split("T")[0]);
                    } else {
                      setCheckOut(date.toISOString().split("T")[0]);
                    }
                  }}
                  excludeDates={disabledDates}
                  minDate={
                    checkIn
                      ? new Date(new Date(checkIn).getTime() + 86400000)
                      : new Date()
                  }
                  maxDate={
                    new Date(new Date().setMonth(new Date().getMonth() + 6))
                  }
                  disabled={!checkIn}
                  placeholderText="dd/mm/yy"
                  dateFormat="dd/MM/yyyy"
                  className="w-full border border-gray-300 rounded-lg p-3"
                  customInput={
                    <input
                      type="text"
                      readOnly
                      className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-orange-400"
                    />
                  }
                />
              </div>

              {/* Number of People */}
              <div className="col-span-12 sm:col-span-4 max-w-[150px]">
                <label
                  htmlFor="numPeople"
                  className="block mb-2 font-semibold text-gray-700 flex items-center gap-2"
                >
                  <FaUsers className="text-orange-500" /> People{" "}
                  <span className="text-red-500 text-md font-normal">*</span>
                </label>
                <input
                  required
                  type="number"
                  id="numPeople"
                  value={numPeople}
                  min={1}
                  max={3}
                  onChange={(e) => {
                    let value = parseInt(e.target.value);

                    if (isNaN(value) || value < 1) {
                      setNumPeople(1);
                    } else if (value > 3) {
                      setNumPeople(3); // Auto-correct to 3 if more than max
                    } else {
                      setNumPeople(value);
                    }
                  }}
                  className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-orange-400"
                />
              </div>
            </div>
            {/* Purpose of Visit */}
            <div className="col-span-12">
              <label
                htmlFor="purpose"
                className="block mb-2 font-semibold text-gray-700 flex items-center gap-2"
              >
                <FaRegStickyNote className="text-orange-500" /> Purpose of Visit{" "}
                <span className="text-red-500 text-md font-normal">*</span>
              </label>
              <textarea
                id="purpose"
                value={visitPurpose}
                onChange={(e) => setVisitPurpose(e.target.value)}
                placeholder="E.g. Family trip, Business meeting, Pilgrimage..."
                rows={3}
                className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-orange-400 resize-none"
              ></textarea>
            </div>

            {/* Total Price */}
            <div className="mt-1 mb-2 p-4 rounded-lg bg-orange-50 border border-orange-200 flex items-center gap-3">
              {numDays > 0 ? (
                <>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-8 w-8 text-orange-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8c-1.657 0-3 .895-3 2v4h6v-4c0-1.105-1.343-2-3-2z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 12v4m-4-4v4m8-4v4m-8 0h8"
                    />
                  </svg>
                  <p className="text-2xl font-extrabold text-orange-600 leading-tight">
                    Total: ₹{totalPrice.toFixed(2)}{" "}
                    <span className="text-lg font-semibold text-orange-700">
                      for {numDays} night{numDays > 1 ? "s" : ""}
                    </span>
                  </p>
                </>
              ) : (
                <p className="text-gray-500 italic text-lg">
                  Please select valid check-in and check-out dates
                </p>
              )}
            </div>
          </div>
          <GooglePayButton
            amount={totalPrice}
            onPaymentSuccess={onPaymentSuccess}
            onPaymentError={onPaymentError}
            disabled={
              !checkIn ||
              !checkOut ||
              !numPeople ||
              visitPurpose.trim().length === 0
            }
          />
        </section>
      </main>
    </div>
  );
}
